import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Pagination,
  Tab,
  Tabs,
} from "@mui/material";
import { useTheme } from "@material-ui/core";
import AddUser from "./AddUser";
import {
  UseCompanyWorkspaces,
  UseUsersFullData,
} from "../../hooks/company";
import { useColumnPreferences, DEFAULT_COLUMNS } from "./useColumnPreferences";
import { useTeams } from "./useTeams";
import CustomizeViewModal from "./CustomizeViewModal";
import { ARCHIVE_COLLEAGUE } from "../../api/user";
import { getTeams, assignUserTeam } from "../../redux/teams/actions";
import { updateCompanyUser } from "../../redux/company/actions";
import { useDispatch } from "react-redux";
import User from "./User";
import { debounce } from "lodash";
import SoftInput from "../../components/SoftInput";
import SoftBox from "../../components/SoftBox";
import SoftSelect from "../../components/SoftSelect";
import { ReactComponent as SearchSvg } from "../../img/search.svg";
import SoftTypography from "../../components/SoftTypography";
import colors from "../../assets/theme/base/colors";
import typography from "../../assets/theme/base/typography";
import borders from "../../assets/theme/base/borders";
// import { UseUserDetails } from "../../hooks/auth";

const LIMIT = 10;

/**
 * Custom react-select Option — renders each workspace as a "rail" item:
 * 3px brand-color rail on the left edge of the selected item, plus a soft
 * brand-tinted left-to-right gradient. Hover gets a faint version. No item
 * background, no border. Premium-quiet treatment.
 */
const RailOption = ({ data, isSelected, isFocused, innerRef, innerProps }) => {
  const railColor = "var(--gmb-primary, #1B598E)";
  const railSoft  = "rgba(27, 89, 142, 0.30)";
  const tintFade  = "linear-gradient(90deg, rgba(27,89,142,0.07), transparent 70%)";

  let itemBg = "transparent";
  if (isSelected) itemBg = tintFade;
  else if (isFocused) itemBg = "#F8FAFC";

  let railBg = "transparent";
  if (isSelected) railBg = railColor;
  else if (isFocused) railBg = railSoft;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        position: "relative",
        padding: "10px 14px 10px 16px",
        fontFamily: "DM Sans",
        fontSize: "12.5px",
        fontWeight: 500,
        color: isSelected ? railColor : "#475467",
        background: itemBg,
        cursor: "pointer",
        transition: "background 140ms, color 140ms",
        userSelect: "none",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: "0 3px 3px 0",
          background: railBg,
          transition: "background 140ms",
        }}
      />
      {data.label}
    </div>
  );
};

const getGroupName = (groupId, groups) => {
  const group = groups.find(({ _id }) => _id === groupId);
  return group?.name || "";
};

const searchUser = (searchKey, data, groups) => {
  const key = searchKey.toLowerCase();
  const filteredData = data.filter((user) => {
    return (
      user?.firstName?.toLowerCase().includes(key) ||
      user?.lastName?.toLowerCase().includes(key) ||
      user?.email?.toString().toLowerCase().includes(key) ||
      getGroupName(user?.groupId, groups).toLowerCase().includes(key) ||
      (user?.isAdmin ? "admin" : "").includes(key)
    );
  });
  return filteredData;
};

const UmTable = () => {
  const allWorkspaces = UseCompanyWorkspaces(true);
  const workspaces = useMemo(
    () => [...allWorkspaces].sort((a, b) => a.name.localeCompare(b.name)),
    [allWorkspaces]
  );
  // Flatten parent/child workspaces into one option list (with sub-workspace
  // group labels), for both the row's "Workspace" cell lookup and its
  // "Change workspace" select. Previously this reduce ran once PER ROW on
  // every render (every row recomputing the same result from scratch), which
  // made the whole players table redo O(rows × workspaces) work any time it
  // re-rendered. Computed once here instead.
  const workspaceRowOptions = useMemo(() => {
    const parentWorkspaces = workspaces.filter((w) => !w.parentId);
    return parentWorkspaces.reduce((acc, parent) => {
      acc.push({ ...parent, value: parent._id, label: parent.name });
      const children = workspaces.filter((w) => w.parentId === parent._id);
      if (children.length > 0) {
        acc.push({
          value: "",
          label: `\u00A0\u00A0\u00A0\u00A0${parent.name} Sub-Workspaces`,
          isDisabled: true,
        });
        children.forEach((child) => {
          acc.push({
            ...child,
            value: child._id,
            label: `\u00A0\u00A0\u00A0\u00A0${child.name}`,
            displayLabel: child.name,
          });
        });
      }
      return acc;
    }, []);
  }, [workspaces]);
  const allUsers = UseUsersFullData();
  // The "first admin" (earliest-created admin) is locked out of the row
  // actions menu company-wide. This must be derived from the full,
  // unfiltered user list — deriving it per-row from the searched/paginated
  // slice (as before) would crown whichever admin happens to still be
  // visible as "first", wrongly locking or unlocking rows depending on what
  // page/search is active. Also previously recomputed (filter+sort) once per
  // row on every render instead of once here.
  const firstAdminUserId = useMemo(() => {
    const firstAdmin = allUsers
      .filter((u) => u.isAdmin)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0];
    return firstAdmin?.userId;
  }, [allUsers]);
  // const theme = useTheme();
  const [search, setSearch] = useState("");
  const [showTab, setShowTab] = useState(0); // 0: Active, 1: Archived
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState('All');
  const [activePlayersCount, setActivePlayersCount] = useState(0);
  const [inactivePlayersCount, setInactivePlayersCount] = useState(0);

  // State for active/archived tables
  const [activeUsers, setActiveUsers] = useState([]);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [filteredActiveUsers, setFilteredActiveUsers] = useState([]);
  const [filteredArchivedUsers, setFilteredArchivedUsers] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [archivedPage, setArchivedPage] = useState(1);
  const [totalActivePages, setTotalActivePages] = useState(1);
  const [totalArchivedPages, setTotalArchivedPages] = useState(1);

  // const { light } = colors;
  // const { size, fontWeightBold } = typography;
  // const { borderWidth } = borders;

  // Column visibility/order driven by user prefs (localStorage).
  const columnPrefs = useColumnPreferences();
  const visibleColumnKeys = useMemo(
    () => new Set(columnPrefs.columns.filter((c) => c.visible).map((c) => c.key)),
    [columnPrefs.columns],
  );
  const visibleColumnOrder = useMemo(
    () => columnPrefs.columns.filter((c) => c.visible).map((c) => c.key),
    [columnPrefs.columns],
  );
  // Header rendering — only show columns the user has enabled, in saved order.
  const columns = visibleColumnOrder.map((key) => {
    const meta = DEFAULT_COLUMNS.find((c) => c.key === key);
    return { name: meta?.label || key, key };
  });

  // `user.team` is now a real backend field carried on every user document,
  // so no local override merge is needed. Keep `usersWithTeams` as an alias
  // for downstream readability and easy revert if a future field needs
  // hydration before render.
  const usersWithTeams = allUsers;

  // Teams come from Redux (`GET /team` populated by the teams saga).
  const teamsApi = useTeams(usersWithTeams);
  const dispatch = useDispatch();

  // Pull the team list once on mount; saga is idempotent so re-mounts are safe.
  useEffect(() => {
    dispatch(getTeams());
  }, [dispatch]);

  // Dropdown write: dispatches the assign saga, which calls
  // PATCH /user/:id/team and then refreshes the users dictionary so the row
  // updates from a single source of truth (Redux companyReducer). Patch the
  // dictionary optimistically first — the saga's own refetch can take a
  // moment, and without this the dropdown would flip back to the old team
  // until it resolves.
  const assignUserToTeam = (userId, teamName) => {
    if (!userId) return;
    dispatch(updateCompanyUser(userId, { team: teamName || "" }));
    dispatch(assignUserTeam(userId, teamName || ""));
  };

  // Customize-view modal open state
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Populate workspace select options
  useEffect(() => {
    if (workspaces?.length) {
      const options = workspaces.map(workspace => ({
        value: workspace._id,
        label: workspace.name
      }));
      setWorkspaceOptions(options);
    }
  }, [workspaces]);

  // Compute active and archived users
  useEffect(() => {
    let filteredActive = allUsers.filter(
      user => !user.isArchived
    );
    let filteredArchived = allUsers.filter(
      user => user.isArchived
    );

    if (currentWorkspace !== 'All') {
      filteredActive = filteredActive.filter(user => user.groupId === currentWorkspace);
      filteredArchived = filteredArchived.filter(user => user.groupId === currentWorkspace);
    }

    filteredActive.sort((a, b) => {
      if (!b.groupId) return -1;
      return a.firstName.localeCompare(b.firstName);
    });
    filteredArchived.sort((a, b) => {
      if (!b.groupId) return -1;
      return a.firstName.localeCompare(b.firstName);
    });

    setActiveUsers(filteredActive);
    setArchivedUsers(filteredArchived);
    setActivePlayersCount(filteredActive.length);
    setInactivePlayersCount(filteredArchived.length);

    // Reset pagination and search when workspace changes or users update
    setActivePage(1);
    setArchivedPage(1);

    // Set filtered initial slices
    setFilteredActiveUsers(filteredActive.slice(0, LIMIT));
    setTotalActivePages(Math.ceil(filteredActive.length / LIMIT));
    setFilteredArchivedUsers(filteredArchived.slice(0, LIMIT));
    setTotalArchivedPages(Math.ceil(filteredArchived.length / LIMIT));
  }, [allUsers, currentWorkspace]);

  // Search and filtering logic (debounced)
  const debounceFilter = debounce((searchKey) => {
    // Show for the current tab only
    if (showTab === 0) {
      const filtered = searchUser(searchKey, activeUsers, workspaces);
      setFilteredActiveUsers(filtered.slice(0, LIMIT));
      setTotalActivePages(Math.ceil(filtered.length / LIMIT));
      setActivePage(1);
    } else {
      const filtered = searchUser(searchKey, archivedUsers, workspaces);
      setFilteredArchivedUsers(filtered.slice(0, LIMIT));
      setTotalArchivedPages(Math.ceil(filtered.length / LIMIT));
      setArchivedPage(1);
    }
  }, 100);

  // Page change handler for separated tables
  const handleActivePageChange = (event, value) => {
    setActivePage(value);
    const start = (value - 1) * LIMIT;
    const end = value * LIMIT;
    setFilteredActiveUsers(activeUsers.slice(start, end));
  };

  const handleArchivedPageChange = (event, value) => {
    setArchivedPage(value);
    const start = (value - 1) * LIMIT;
    const end = value * LIMIT;
    setFilteredArchivedUsers(archivedUsers.slice(start, end));
  };

  // Handle user archiving (affects both tables)
  const archiveUser = async (id, isArchived) => {
    // Immediate UI feedback for active or archived set
    const idxActive = activeUsers.findIndex((user) => user.userId === id);
    const idxArchived = archivedUsers.findIndex((user) => user.userId === id);

    if (idxActive !== -1) {
      // Move user from active to archived
      const updated = [...activeUsers];
      updated[idxActive].isArchived = isArchived;
      setActiveUsers(updated.filter(user => user.userId !== id));
      // Add to archived on next re-render from allUsers update
    }
    if (idxArchived !== -1) {
      const updated = [...archivedUsers];
      updated[idxArchived].isArchived = isArchived;
      setArchivedUsers(updated);
    }
    // Patch the shared Redux dictionary too, not just this table's own local
    // state -- other screens read UseUsersFullData() straight from Redux, and
    // without this the only way to make them see the change was the full
    // `window.location.reload()` ArchivePlayer.jsx used to do after every
    // single archive/unarchive click.
    dispatch(updateCompanyUser(id, { isArchived }));
    await ARCHIVE_COLLEAGUE(id, isArchived);
  };

  // Tab and search interaction (clears search and paginates when switching)
  useEffect(() => {
    setSearch('');
    if (showTab === 0) {
      setFilteredActiveUsers(activeUsers.slice(0, LIMIT));
      setTotalActivePages(Math.ceil(activeUsers.length / LIMIT));
      setActivePage(1);
    } else {
      setFilteredArchivedUsers(archivedUsers.slice(0, LIMIT));
      setTotalArchivedPages(Math.ceil(archivedUsers.length / LIMIT));
      setArchivedPage(1);
    }
  }, [showTab, activeUsers, archivedUsers]);

  return (
    <SoftBox sx={{ fontFamily: '"DM Sans", system-ui, sans-serif', marginTop: "24px" }}>
      {/* Top toolbar — segmented tabs left, filters + actions right */}
      <SoftBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: "1rem",
        }}
      >
        {/* Active / Inactive segmented tabs */}
        <SoftBox
          sx={{
            display: "inline-flex",
            background: "#FFFFFF",
            border: "1px solid #E4E7EC",
            borderRadius: "10px",
            padding: "3px",
            gap: "2px",
            height: "40px",
            alignItems: "center",
          }}
        >
          {[
            { key: 0, label: `Active players (${activePlayersCount})` },
            { key: 1, label: `Inactive players (${inactivePlayersCount})` },
          ].map((tab) => {
            const isOn = showTab === tab.key;
            return (
              <SoftBox
                key={tab.key}
                component="button"
                type="button"
                onClick={() => setShowTab(tab.key)}
                sx={{
                  height: "32px",
                  padding: "0 14px",
                  borderRadius: "7px",
                  border: 0,
                  cursor: "pointer",
                  fontFamily: "DM Sans",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  background: isOn ? "var(--gmb-primary, #1B598E)" : "transparent",
                  color: isOn ? "#FFFFFF" : "#667085",
                  transition: "background 140ms ease, color 140ms ease",
                  "&:hover": { color: isOn ? "#FFFFFF" : "#191D23" },
                }}
              >
                {tab.label}
              </SoftBox>
            );
          })}
        </SoftBox>

        {/* Right side: workspace filter + customize + add */}
        <SoftBox sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {workspaceOptions?.length ? (
            <SoftBox sx={{ width: "180px" }}>
              <SoftSelect
                size="medium"
                options={[
                  { value: "All", label: "All workspaces" },
                  ...workspaceOptions,
                ]}
                defaultValue={{ value: "All", label: "All workspaces" }}
                onChange={(option) => setCurrentWorkspace(option.value)}
                components={{ Option: RailOption }}
                styles={{
                  control: {
                    height: "40px",
                    minHeight: "40px",
                    border: "1px solid #E4E7EC",
                    borderRadius: "10px",
                    background: "#FFFFFF",
                    boxShadow: "none",
                    fontFamily: "DM Sans",
                    fontSize: "13px",
                    fontWeight: 500,
                  },
                  // Frosted-glass menu: translucent white + heavy blur. Width
                  // is wider than the trigger so long workspace names breathe.
                  menu: {
                    width: "280px",
                    minWidth: "280px",
                    background: "rgba(255, 255, 255, 0.72)",
                    backdropFilter: "blur(28px) saturate(180%)",
                    WebkitBackdropFilter: "blur(28px) saturate(180%)",
                    border: "0.5px solid rgba(255, 255, 255, 0.7)",
                    borderRadius: "14px",
                    padding: "6px",
                    boxShadow:
                      "0 1px 0 0 rgba(255,255,255,0.6) inset, " +
                      "0 30px 60px -20px rgba(16,24,40,0.18), " +
                      "0 8px 20px -10px rgba(16,24,40,0.10)",
                    overflow: "hidden",
                  },
                  menuList: {
                    maxHeight: "260px",
                    overflowX: "hidden",
                    overflowY: "auto",
                    padding: 0,
                    // Thin custom scrollbar — webkit + firefox.
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(27, 89, 142, 0.25) transparent",
                  },
                  option: {
                    background: "transparent",
                    color: "#475467",
                    fontFamily: "DM Sans",
                    fontSize: "13px",
                    fontWeight: 500,
                    padding: "8px 10px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    minWidth: 0,
                    width: "100%",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  },
                }}
              />
            </SoftBox>
          ) : null}
          {/* Customize view (column visibility + manage teams) */}
          <SoftBox
            component="button"
            type="button"
            onClick={() => setCustomizeOpen(true)}
            aria-label="Customize view"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              border: "1px solid #E4E7EC",
              background: "#fff",
              cursor: "pointer",
              color: "#475467",
              transition: "border-color 120ms ease, color 120ms ease",
              "&:hover": { borderColor: "#9ba3ad", color: "#1f2937" },
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="14" y2="12" />
              <line x1="4" y1="18" x2="9" y2="18" />
              <circle cx="18" cy="14" r="2.5" />
            </svg>
          </SoftBox>
          <AddUser />
        </SoftBox>
      </SoftBox>

      {/* Table card */}
      <SoftBox
        sx={{
          background: "#FFF",
          border: "1px solid #E4E7EC",
          borderRadius: "12px",
          padding: "1rem 1.25rem",
        }}
      >
        <Grid container sx={{ marginBottom: "1rem" }}>
          <Grid item mini={12}>
            <SoftInput
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debounceFilter(e.target.value);
              }}
              sx={{
                // Don't add an outer border here — SoftInput already wraps its
                // own MuiInputBase border. Stacking two boxes produced the
                // mid-input "line" artifact.
                maxWidth: "320px",
                "& .MuiInputBase-root": {
                  height: "40px",
                  borderRadius: "10px",
                  border: "1px solid #E4E7EC",
                  background: "#FFFFFF",
                },
                "& input": { fontFamily: "DM Sans", fontSize: "13px", fontWeight: 500 },
              }}
              icon={{
                component: <SearchSvg />,
                direction: "left",
              }}
            />
          </Grid>
        </Grid>
        <hr className="my-3 divide-slate-300" />
        {/* Show correct table based on tab.
            Wrapper enables horizontal scroll once the viewport is too narrow
            (~999px) for the columns to fit — beyond that point the Actions
            column was overflowing the card. min-width keeps columns at their
            natural size instead of collapsing on top of each other. */}
        <SoftBox sx={{ overflowX: "auto", overflowY: "visible", margin: "0 -1.25rem", padding: "0 1.25rem" }}>
        <Table sx={{ minWidth: "880px" }}>
          <SoftBox component="thead">
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  style={{
                    // Name and Email are free text of varying length — they
                    // read as a column only when flush left. Everything else
                    // stays centred.
                    textAlign: column.key === "Name" || column.key === "Email" ? "left" : "center",
                  }}
                >
                  {column.name}
                </TableCell>
              ))}
            </TableRow>
          </SoftBox>
          <TableBody>
            {showTab === 0
              ? filteredActiveUsers.map((user) => {
                  const isFirstAdmin = firstAdminUserId === user.userId;
                  // `user.team` is sourced from the backend now (Redux company
                  // user dictionary); no local override merge required.
                  const userWithTeam = { ...user, team: user.team || "" };
                  return (
                    <User
                      key={user.userId}
                      user={userWithTeam}
                      workspaces={workspaceRowOptions}
                      archiveUser={archiveUser}
                      isFirstAdmin={isFirstAdmin}
                      visibleColumns={visibleColumnKeys}
                      teams={teamsApi.teams}
                      assignUserToTeam={assignUserToTeam}
                      searchTerm={search}
                    />
                  );
                })
              : filteredArchivedUsers.map((user) => {
                  const isFirstAdmin = firstAdminUserId === user.userId;
                  const userWithTeam = { ...user, team: user.team || "" };
                  return (
                    <User
                      key={user.userId}
                      user={userWithTeam}
                      workspaces={workspaceRowOptions}
                      archiveUser={archiveUser}
                      isFirstAdmin={isFirstAdmin}
                      visibleColumns={visibleColumnKeys}
                      teams={teamsApi.teams}
                      assignUserToTeam={assignUserToTeam}
                      searchTerm={search}
                    />
                  );
                })}
          </TableBody>
        </Table>
        </SoftBox>
        {showTab === 0 && totalActivePages > 1 && (
          <SoftBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <SoftBox sx={{ flex: 1 }} />
            <Pagination
              count={totalActivePages}
              page={activePage}
              onChange={handleActivePageChange}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '4px',
                },
                '& .MuiPaginationItem-page:hover': {
                  borderRadius: '4px',
                },
                '& .Mui-selected': {
                  borderRadius: '4px',
                }
              }}
            />
            <SoftBox sx={{ flex: 1, textAlign: 'right' }}>
              <SoftTypography variant="body2" color="text" sx={{ color: "#000000" }}>
                Showing {filteredActiveUsers.length} of {activeUsers.length} Results
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        )}
        {showTab === 1 && totalArchivedPages > 1 && (
          <SoftBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <SoftBox sx={{ flex: 1 }} />
            <Pagination
              count={totalArchivedPages}
              page={archivedPage}
              onChange={handleArchivedPageChange}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '4px',
                },
                '& .MuiPaginationItem-page:hover': {
                  borderRadius: '4px',
                },
                '& .Mui-selected': {
                  borderRadius: '4px',
                }
              }}
            />
            <SoftBox sx={{ flex: 1, textAlign: 'right' }}>
              <SoftTypography variant="body2" color="text" sx={{ color: "#000000" }}>
                Showing {filteredArchivedUsers.length} of {archivedUsers.length} Results
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        )}
      </SoftBox>

      {/* Customize view modal — column visibility + team management */}
      <CustomizeViewModal
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        columnPrefs={columnPrefs}
        teamsApi={teamsApi}
        users={usersWithTeams}
        assignUserToTeam={assignUserToTeam}
      />
    </SoftBox>
  );
};

export default UmTable;
